import React, { useState, useEffect, useRef } from 'react';
import { useAdminSpreadsheet } from '../../../hooks/useAdminSpreadsheet';
import { useToast } from '../../../hooks/useToast';
import { ToastNotification } from '../../../components/ui/ToastNotification';
import { Property } from '../../../types';
import { SpinnerIcon, ExclamationTriangleIcon } from '../../../components/Icons';
import { OperationSpreadsheetHeader } from './OperationSpreadsheetHeader';
import { OperationSpreadsheetTable } from './OperationSpreadsheetTable';
import { OperationExportModal } from './OperationExportModal';
import { MassUpdateModal } from './MassUpdateModal';
import { ScheduleRangeSelectorModal } from './operation/ScheduleRangeSelectorModal';
import { INTERNAL_FIELD_MAP } from './operation/OperationConstants';

export const AdminPropertySpreadsheetSection: React.FC = () => {
    const { 
        properties, allowedOwners, loading, loadingMore, error, hasMore, filters,
        updateFilters, updateProperty, loadMore, fetchAllFilteredProperties, refresh
    } = useAdminSpreadsheet(true);

    const { toast, showSuccess, showError, hideToast } = useToast();
    const [localEdits, setLocalEdits] = useState<Record<string, Partial<Property>>>({});
    const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isMassUpdateModalOpen, setIsMassUpdateModalOpen] = useState(false);
    const [scheduleEditorConfig, setScheduleEditorConfig] = useState<{ id: string; val: string } | null>(null);
    const [brpInput, setBrpInput] = useState(filters.brpCode);
    const [codeInput, setCodeInput] = useState(filters.propertyCode);
    const loaderRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => { if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) loadMore(); },
            { threshold: 0.1 }
        );
        if (loaderRef.current) observer.observe(loaderRef.current);
        return () => observer.disconnect();
    }, [hasMore, loading, loadingMore, loadMore]);

    const handleLocalEdit = (id: string, field: keyof Property, value: any) => {
        setLocalEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
    };

    const handleSave = async (id: string) => {
        const edits = localEdits[id];
        if (!edits) return;
        setSavingIds(prev => new Set(prev).add(id));
        const dataToSave: any = {};
        Object.entries(edits).forEach(([key, value]) => {
            const targetKey = INTERNAL_FIELD_MAP[key] || key;
            dataToSave[targetKey] = value;
        });

        if (dataToSave.valor_administracion !== undefined) {
            dataToSave.valor_administracion = Number(String(dataToSave.valor_administracion).replace(/[^0-9]/g, '')) || 0;
        }

        const success = await updateProperty(id, dataToSave);
        if (success) {
            setLocalEdits(prev => { const next = { ...prev }; delete next[id]; return next; });
            showSuccess('Cambio guardado exitosamente. Se reflejará en máximo 5 minutos.');
        } else {
            showError('Error al guardar los cambios');
        }
        setSavingIds(prev => { const next = new Set(prev); next.delete(id); return next; });
    };

    if (loading && properties.length === 0) return (
        <div className="flex flex-col items-center py-32 animate-pulse">
            <SpinnerIcon className="w-12 h-12 text-sky-600 mb-4" />
            <p className="text-gray-500 font-black uppercase text-xs tracking-widest">Sincronizando inventario...</p>
        </div>
    );

    return (
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden animate-fade-in relative">
            <ToastNotification toast={toast} onClose={hideToast} duration={4000} />
            <OperationSpreadsheetHeader 
                filters={filters} 
                updateFilters={updateFilters} 
                visibleCount={properties.length}
                onOpenMassUpdate={() => setIsMassUpdateModalOpen(true)}
                onOpenExport={() => setIsExportModalOpen(true)}
                codeInput={codeInput}
                setCodeInput={setCodeInput}
                brpInput={brpInput}
                setBrpInput={setBrpInput}
            />

            {error && (
                <div className="m-8 p-4 bg-red-50 border border-red-100 rounded-2xl text-red-700 flex items-center gap-4 text-sm font-medium">
                    <ExclamationTriangleIcon className="w-6 h-6 flex-shrink-0" />
                    {error}
                </div>
            )}

            <OperationSpreadsheetTable 
                properties={properties}
                allowedOwners={allowedOwners}
                filters={filters}
                updateFilters={updateFilters}
                localEdits={localEdits}
                savingIds={savingIds}
                onLocalEdit={handleLocalEdit}
                onSave={handleSave}
                codeInput={codeInput}
                setCodeInput={setCodeInput}
                brpInput={brpInput}
                setBrpInput={setBrpInput}
                onOpenScheduleEditor={(id, val) => setScheduleEditorConfig({ id, val })}
            />

            <div ref={loaderRef} className="py-8 flex justify-center border-t border-gray-100 bg-gray-50/30">
                {loadingMore ? (
                    <div className="flex items-center gap-2 text-sky-600">
                        <SpinnerIcon className="w-4 h-4 animate-spin" />
                        <span className="text-[10px] font-black uppercase tracking-widest">Cargando más registros...</span>
                    </div>
                ) : !hasMore && properties.length > 0 ? (
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Fin del inventario</span>
                ) : null}
            </div>

            <OperationExportModal 
                isOpen={isExportModalOpen} 
                onClose={() => setIsExportModalOpen(false)} 
                onFetchAll={fetchAllFilteredProperties} 
            />

            <MassUpdateModal 
                isOpen={isMassUpdateModalOpen} 
                onClose={() => setIsMassUpdateModalOpen(false)} 
                allowedOwners={allowedOwners}
                onComplete={() => { setIsMassUpdateModalOpen(false); refresh(); }}
            />

            <ScheduleRangeSelectorModal 
                isOpen={!!scheduleEditorConfig}
                onClose={() => setScheduleEditorConfig(null)}
                initialValue={scheduleEditorConfig?.val || ''}
                onSave={(newVal) => {
                    if (scheduleEditorConfig) handleLocalEdit(scheduleEditorConfig.id, 'disponibilidad_visitas', newVal);
                }}
            />
        </div>
    );
};